<template>
  <b-container fluid class="lz-controls">
    <div class="row">
      <div class="col-lg-12 text-right lz-holiday-actions">
        <b-button
          v-if="canApplyMandatoryLeave"
          class="lz-close-btn"
          variant="outline-secondary"
          size="sm"
          @click="$root.$emit('open-mandatory-leave-modal')">
          Mandatory Leave
        </b-button>
        <b-button
          :id="btn.id"
          class="lz-trigger-btn"
          :variant="btn.variant"
          size="sm"
          @click="toggleModal">
          {{ btn.text }}
        </b-button>
      </div>
    </div>
    <b-modal
      centered
      scrollable
      no-close-on-esc
      button-size="sm"
      modal-class="lz-activity-modal lz-controls"
      content-class="lz-modal-content"
      body-class="lz-modal-body"
      :ref="modal.id"
      :header-bg-variant="modal.styles.headerBgVariant"
      :header-text-variant="modal.styles.headerTextVariant"
      :body-bg-variant="modal.styles.bodyBgVariant"
      :body-text-variant="modal.styles.bodyTextVariant"
      :footer-bg-variant="modal.styles.footerBgVariant"
      :footer-text-variant="modal.styles.footerTextVariant">
      <template #modal-title>{{ modal.title }}</template>
      <b-form class="lz-holiday-form">
        <b-form-group
          label="Team member"
          label-for="teamMembersSearchInput"
          label-cols-lg="3"
          label-class="lz-form-label">
          <treeselect
            v-if="modal.teamMembersTreeSettings.options.length"
            id="teamMembersSearchInput"
            class="team-members-select"
            :value="modal.teamMembersTreeSettings.value"
            :multiple="modal.teamMembersTreeSettings.multiple"
            :clearable="modal.teamMembersTreeSettings.clearable"
            :searchable="modal.teamMembersTreeSettings.searchable"
            :open-on-click="modal.teamMembersTreeSettings.openOnClick"
            :close-on-select="modal.teamMembersTreeSettings.closeOnSelect"
            :limit="modal.teamMembersTreeSettings.limit"
            :options="modal.teamMembersTreeSettings.options"
            @select="userChanged" />
          <div class="lz-form-loading" v-else>
            <b-spinner small type="grow" label="Loading..."></b-spinner>
            <span>Loading team members...</span>
          </div>
        </b-form-group>

        <b-form-group
          label="Holiday type"
          label-for="type"
          label-cols-lg="3"
          label-class="lz-form-label">
          <b-form-select
            id="type"
            v-model="modal.data.type"
            :disabled="!modal.data.user"
            :options="data.types"
            required>
            <template #first>
              <b-form-select-option :value="null" disabled
                >Select a holiday type</b-form-select-option
              >
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group
          label="Date"
          label-for="date"
          label-cols-lg="3"
          label-class="lz-form-label">
          <el-date-picker
            id="date"
            v-model="modal.data.dates"
            type="date"
            placeholder="Pick a day"
            class="lz-form-date"
            format="dd/MM/yyyy"
            value-format="yyyy-MM-dd"
            :disabled="!modal.data.user"
            :picker-options="holidayPickerOptions"
            v-if="!data.showDateRangeInput"></el-date-picker>
          <el-date-picker
            id="date"
            v-model="modal.data.dates"
            type="daterange"
            range-separator=""
            start-placeholder="From"
            end-placeholder="To"
            class="lz-form-date"
            format="dd/MM/yyyy"
            value-format="yyyy-MM-dd"
            :disabled="!modal.data.user"
            :picker-options="holidayPickerOptions"
            v-else>
          </el-date-picker>
        </b-form-group>

        <b-form-group
          v-if="modal.data.allowance.length"
          label="Balance"
          label-cols-lg="3"
          label-class="lz-form-label"
          class="mb-0">
          <div class="lz-balance-chips">
            <span
              v-for="yearData in modal.data.allowance"
              :key="yearData.year"
              class="lz-balance-chip"
              :class="yearData.balance >= 0 ? 'is-positive' : 'is-negative'">
              <span class="lz-balance-year">{{ yearData.year }}</span>
              <span class="lz-balance-value">{{ yearData.balance }}</span>
            </span>
          </div>
        </b-form-group>
      </b-form>
      <template #modal-footer>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="lz-close-btn"
          @click="$refs[modal.id].hide()">
          Close
        </b-button>
        <b-button
          :disabled="modal.loading"
          size="sm"
          variant="primary"
          class="lz-close-btn"
          @click="addNewHoliday()">
          {{ modal.submitBtnText }}
        </b-button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect'
import ServicesOld from '../../../services/index.vue'
import Services from '../../../services/main.service.vue'
import {
  isWeekend,
  format as dateFormat,
  differenceInCalendarDays,
  differenceInQuarters
} from 'date-fns'

export default {
  name: 'CreateHoliday',
  components: {
    Treeselect
  },
  data() {
    return {
      data: {
        types: [],
        bankHolidays: [],
        teamMembers: [],
        showDateRangeInput: false
      },
      btn: {
        id: 'add-holiday-btn',
        text: 'Add Holiday',
        variant: 'info'
      },
      modal: {
        id: 'add-holiday-modal',
        title: 'New Holiday',
        submitBtnText: 'Submit',
        loading: false,
        // Matches the "New Leave Request" dialog: white chrome, with the
        // tinted body coming from .lz-modal-body rather than a variant.
        styles: {
          headerBgVariant: 'white',
          headerTextVariant: 'dark',
          bodyBgVariant: 'white',
          bodyTextVariant: 'dark',
          footerBgVariant: 'white',
          footerTextVariant: 'dark'
        },
        data: {
          user: null,
          userFullname: null,
          dates: null,
          type: null,
          holidayType: null,
          holidayHalfDayMeridiem: null,
          totalDays: 0,
          totalBankHolidaysInDates: 0,
          allowanceData: [],
          allowance: [],
          allowanceKey: null,
          userAllowances: {}
        },
        teamMembersTreeSettings: {
          value: 'default',
          multiple: false,
          clearable: false,
          searchable: true,
          openOnClick: true,
          closeOnSelect: true,
          limit: 1,
          options: []
        }
      }
    }
  },
  computed: {
    canApplyMandatoryLeave() {
      return this.$store.getters.userAuth?.hasSuperAdminPrivileges ?? false
    },
    holidayPickerOptions() {
      return {
        firstDayOfWeek: 1,
        disabledDate: (dt) => {
          const isBankHoliday = this.data.bankHolidays.includes(
            dateFormat(new Date(dt), 'yyyy-MM-dd')
          )
          const dtYear = new Date(dt).getFullYear()
          const now = new Date()
          const currentYear = now.getFullYear()
          const sideYear =
            currentYear +
            (!Math.abs(
              differenceInQuarters(
                now,
                new Date(currentYear + 1, 1, 1, 0, 0, 0, 0)
              )
            )
              ? 1
              : -1)
          const sameYear = [currentYear, sideYear].includes(dtYear)
          return isWeekend(new Date(dt)) || isBankHoliday || !sameYear
        }
      }
    }
  },
  methods: {
    async toggleModal() {
      this.$refs[this.modal.id].toggle('#' + this.btn.id)
      if (this.data.bankHolidays.length === 0) {
        const { status, data } = await this.getBankHolidays()
        if (status === 200) {
          this.data.bankHolidays = data.content.map((bh) => bh.date)
        }
      }
      if (this.modal.teamMembersTreeSettings.options.length === 0) {
        await this.SetUsersList()
      }
    },
    async getBankHolidays() {
      return await ServicesOld.GetBankHolidays()
    },
    async SetUsersList() {
      const Users = await Services.Users.Admin.getList()
      const List =
        Users?.data?.content
          ?.filter((user) => user.settings.active)
          ?.map(({ uid, firstName, lastName }) => {
            return {
              id: uid,
              label: `${firstName} ${lastName}`.trim()
            }
          })
          ?.sort((a, b) => {
            if (a.label < b.label) {
              return -1
            }
            if (a.label > b.label) {
              return 1
            }
            return 0
          }) ?? []
      if (List.length) {
        List.unshift({
          id: 'default',
          label: 'Select one...'
        })
      }
      this.modal.teamMembersTreeSettings.options = List
    },
    async userChanged({ id = 'default', label }) {
      this.modal.data.user = id
      this.modal.data.userFullname = label
      const allowances = await this.getAllowance(id)
      const currentYear = new Date().getFullYear()
      // Export Holiday Types based on Remaining Allowance
      const typeDic = {
        CARRYFORWARD: 'Carry Forward',
        INLIEU: 'In-Lieu',
        HALFPERSONAL: 'Half Personal'
      }
      const years = {}
      for (const year in allowances) {
        years[year] = years[year] || {}
        allowances[year].forEach((itm) => {
          for (const group in itm.groups) {
            years[year][group] = {
              value: group,
              text: typeDic[group] ?? itm.value,
              _allowance: itm.groups[group],
              _remains: itm.groups[group] ? itm.remains : 0
            }
            if (group === 'ANNUAL') {
              years[year]['HALFDAYAM'] = {
                value: 'HALFDAYAM',
                text: 'Half Day (AM)',
                _allowance: itm.groups[group],
                _remains: itm.groups[group] ? itm.remains : 0
              }
              years[year]['HALFDAYPM'] = {
                value: 'HALFDAYPM',
                text: 'Half Day (PM)',
                _allowance: itm.groups[group],
                _remains: itm.groups[group] ? itm.remains : 0
              }
            }
          }
        })
      }
      for (const year in years) {
        years[year] = Object.values(years[year]).sort((a, b) => {
          if (a.text < b.text) return -1
          if (a.text > b.text) return 1
          return 0
        })
      }
      this.data.types = years[currentYear]
    },
    typeChanged(val) {
      this.modal.data.dates = null
      this.modal.data.totalDays = 0
      this.modal.data.totalBankHolidaysInDates = 0
      this.modal.data.allowance = []
      const halfdayReg = new RegExp('^HALF.+', 'i')
      this.data.showDateRangeInput = !halfdayReg.test(val)
      this.modal.data.allowanceKey = null
      switch (val) {
        case 'ANNUAL':
          this.modal.title = 'New Annual Holiday'
          this.modal.data.holidayType = val
          this.modal.data.allowanceKey = val
          break
        case 'SICK':
        case 'PERSONAL':
          this.modal.title = 'New Personal/Sick Holiday'
          this.modal.data.holidayType = val
          this.modal.data.allowanceKey = 'PERSONAL'
          break
        case 'HALFDAYAM':
          this.modal.title = 'New Half-Day Holiday (AM)'
          this.modal.data.holidayType = 'HALFDAY'
          this.modal.data.holidayHalfDayMeridiem = 'AM'
          this.modal.data.allowanceKey = 'ANNUAL'
          break
        case 'HALFDAYPM':
          this.modal.title = 'New Half-Day Holiday (PM)'
          this.modal.data.holidayType = 'HALFDAY'
          this.modal.data.holidayHalfDayMeridiem = 'PM'
          this.modal.data.allowanceKey = 'ANNUAL'
          break
        case 'HALFPERSONAL':
          this.modal.title = 'New Half-Day Personal'
          this.modal.data.holidayType = 'HALFPERSONAL'
          this.modal.data.holidayHalfDayMeridiem = 'AM'
          this.modal.data.allowanceKey = 'PERSONAL'
          break
        default:
          this.modal.title = 'New Holiday'
          this.modal.data.holidayType = val
          break
      }
      if (this.modal.data.userFullname) {
        this.modal.title += ' for ' + this.modal.data.userFullname
      }
    },
    datesChanged(dt) {
      this.modal.data.allowance = []
      if (!dt) {
        return
      }
      dt = typeof dt === 'string' ? [dt] : dt
      const start = new Date(dt[0])
      const end = new Date(dt[1] || dt[0])
      const daysInEachYear = {}
      this.modal.data.totalDays =
        this.modal.data.holidayType === 'HALFDAY'
          ? 0.5
          : differenceInCalendarDays(new Date(end), new Date(start)) + 1
      const loop = new Date(start)
      while (loop <= end) {
        const year = loop.getFullYear()
        const dt = dateFormat(loop, 'yyyy-MM-dd')
        const isBankHoliday = this.data.bankHolidays.includes(dt)
        if (isBankHoliday || isWeekend(loop)) {
          this.modal.data.totalDays -= ['HALFDAY', 'HALFPERSONAL'].includes(
            this.modal.data.holidayType
          )
            ? 0.5
            : 1
        } else {
          daysInEachYear[year] = daysInEachYear[year] || 0
          daysInEachYear[year]++
        }
        loop.setDate(loop.getDate() + 1)
      }
      let hasBalance = true
      if (this.modal.data.allowanceKey) {
        for (const year in this.modal.data.allowanceData) {
          const requestedDays = daysInEachYear[year] ?? 0
          const allowance =
            this.modal.data.allowanceData[year].find(
              (data) => data.key === this.modal.data.allowanceKey
            )?.remains ?? 0
          daysInEachYear[year] = allowance - requestedDays
          hasBalance = hasBalance && daysInEachYear[year] >= 0
          this.modal.data.allowance.push({
            year,
            requestedDays,
            allowance,
            balance: allowance - requestedDays
          })
        }
      }
    },
    async getAllowance(userUid) {
      try {
        const response = await ServicesOld.GetHolidayAllowencesByUser(userUid)
        return response?.data?.content ?? {}
      } catch (Exception) {
        throw new Error(Exception)
      }
    },
    async checkUsersList() {
      if (!this.modal.teamMembersTreeSettings.options.length) {
        await this.SetUsersList()
      }
    },
    resetData() {
      this.data.types = []
      this.data.teamMembers = []
      this.modal.teamMembersTreeSettings.options = []
      this.data.bankHolidays = []
      this.modal.data = {
        user: null,
        userFullname: null,
        dates: null,
        type: null,
        holidayType: null,
        holidayHalfDayMeridiem: null,
        totalDays: 0,
        totalBankHolidaysInDates: 0,
        allowanceData: [],
        allowance: null,
        allowanceBgColour: 'transparent'
      }
    },
    addNewHoliday() {
      const { dates, holidayType, holidayHalfDayMeridiem, totalDays, user } =
        this.modal.data
      ServicesOld.AddHolidayByAdmin({
        dates: typeof dates === 'string' ? [dates] : dates,
        type: holidayType,
        holidayHalfDayMeridiem,
        totalDays,
        user,
        notes: 'added by admin.'
      })
        .then((res) => {
          if (res.status === 200) {
            this.resetData()
            this.$refs[this.modal.id].hide()
            setTimeout(() => {
              this.$root.$emit('tab_holidays_selected')
            }, 500)
          }
        })
        .catch((err) => {
          console.log({ err })
        })
    }
  },
  watch: {
    'modal.data.type': {
      handler: 'typeChanged',
      immediate: true
    },
    'modal.data.dates': {
      handler: 'datesChanged',
      immediate: true
    }
  }
}
</script>

<style scoped>
.lz-holiday-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* b-modal renders outside this component's subtree, but the elements below
   are still authored here, so the scoped attribute reaches them. */
.lz-holiday-form >>> .lz-form-label {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  padding-top: 9px;
}

.lz-holiday-form >>> .form-group {
  margin-bottom: 18px;
}

.lz-form-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  font-size: 13.5px;
  font-weight: 600;
  color: #64748b;
}

.lz-form-date {
  width: 100%;
}

.lz-balance-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 6px;
}

.lz-balance-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 12.5px;
  font-weight: 600;
}

.lz-balance-chip.is-positive {
  background-color: rgba(22, 163, 74, 0.08);
  border-color: rgba(22, 163, 74, 0.2);
  color: #15803d;
}

.lz-balance-chip.is-negative {
  background-color: rgba(220, 38, 38, 0.08);
  border-color: rgba(220, 38, 38, 0.2);
  color: #b91c1c;
}

.lz-balance-year {
  opacity: 0.75;
}

.lz-balance-value {
  font-size: 13px;
  font-weight: 700;
}
</style>
